/**
 * AGC: Staking Hook for Origami Battle 2.0
 * AGC: Provides wagmi v2 hooks for StakingManager contract interactions
 * @author AGC (AI-Generated Code) with Cursor
 */

import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt, usePublicClient } from 'wagmi'
import { parseEther, formatEther, parseAbi } from 'viem'
import { useState, useEffect } from 'react'
import { buildBackendUrl } from '../utils/backendUrl'

// AGC: StakingManager contract address from environment
const STAKING_MANAGER_ADDRESS = (import.meta.env.VITE_STAKING_MANAGER_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const OGB_TOKEN_ADDRESS = (import.meta.env.VITE_OGB_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`

// AGC: StakingManager ABI (essential functions only) - using parseAbi for wagmi v2 compatibility
const STAKING_MANAGER_ABI = parseAbi([
  'function stake(uint256 amount) external',
  'function requestUnstake(uint256 amount) external',
  'function completeUnstake(uint256 requestIndex) external',
  'function getStakingInfo(address user) external view returns (uint256 stakedBalance, uint256 stakingScore, uint8 tier, uint256 pendingUnstakes)',
  'function getUnstakeRequest(address user, uint256 requestIndex) external view returns (uint256 amount, uint256 requestedAt, uint256 unlockTime, bool completed)',
  'function canAccessQuests(address user) external view returns (bool)',
  'function getMarketplaceDiscount(address user) external view returns (uint256)',
  'function isLocked(address user) external view returns (bool locked, uint256 unlockTime)',
  'function lockedUntil(address user) external view returns (uint256)',
  'event Staked(address indexed user, uint256 amount, uint256 totalStaked, uint8 tier)',
  'event UnstakeRequested(address indexed user, uint256 amount, uint256 unlockTime)',
  'event Unstaked(address indexed user, uint256 amount, uint256 remainingStaked)',
])

// AGC: OGB Token ABI (for approval) - using parseAbi for wagmi v2 compatibility
const OGB_TOKEN_ABI = parseAbi([
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function balanceOf(address account) external view returns (uint256)',
])

export interface StakingInfo {
  stakedBalance: bigint
  stakingScore: bigint
  tier: 0 | 1 | 2 // NONE = 0, ACCESS = 1, MAX = 2
  pendingUnstakes: bigint
}

export interface UnstakeRequest {
  amount: bigint
  requestedAt: bigint
  unlockTime: bigint
  completed: boolean
}

export function useStaking(userAddress?: `0x${string}`) {
  const { address, isConnected } = useAccount()
  const { writeContractAsync, isPending } = useWriteContract()
  const publicClient = usePublicClient()
  const [pendingTxHash, setPendingTxHash] = useState<`0x${string}` | null>(null)
  
  // AGC: Use provided address or connected address
  const effectiveAddress = (userAddress || address) as `0x${string}` | undefined

  // AGC: Read staking info
  const { data: rawStakingInfo, refetch: refetchStakingInfo, error: stakingInfoError } = useReadContract({
    address: STAKING_MANAGER_ADDRESS,
    abi: STAKING_MANAGER_ABI,
    functionName: 'getStakingInfo',
    args: effectiveAddress ? [effectiveAddress] : undefined,
    query: {
      enabled: !!effectiveAddress && isConnected && STAKING_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })
  
  // AGC: Process tuple return into StakingInfo object
  // AGC: Contract returns: (uint256 stakedBalance, uint256 stakingScore, uint8 tier, uint256 pendingUnstakes)
  const stakingInfo: StakingInfo | undefined = rawStakingInfo && Array.isArray(rawStakingInfo) && rawStakingInfo.length >= 4
    ? {
        stakedBalance: rawStakingInfo[0] as bigint,
        stakingScore: rawStakingInfo[1] as bigint,
        tier: Number(rawStakingInfo[2]) as 0 | 1 | 2,
        pendingUnstakes: rawStakingInfo[3] as bigint,
      }
    : undefined
  
  // AGC: Debug logging
  useEffect(() => {
    if (effectiveAddress && isConnected) {
      console.log('🔍 useStaking Debug:', {
        address: effectiveAddress,
        stakingManagerAddress: STAKING_MANAGER_ADDRESS,
        rawData: rawStakingInfo,
        processed: stakingInfo,
        error: stakingInfoError,
      })
    }
  }, [effectiveAddress, isConnected, rawStakingInfo, stakingInfo, stakingInfoError])

  // AGC: Read OGB balance
  const { data: ogbBalance } = useReadContract({
    address: OGB_TOKEN_ADDRESS,
    abi: OGB_TOKEN_ABI,
    functionName: 'balanceOf',
    args: effectiveAddress ? [effectiveAddress] : undefined,
    query: {
      enabled: !!effectiveAddress && isConnected,
    },
  }) as { data: bigint | undefined }

  // AGC: Read OGB allowance for StakingManager
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: OGB_TOKEN_ADDRESS,
    abi: OGB_TOKEN_ABI,
    functionName: 'allowance',
    args: effectiveAddress && STAKING_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000' ? [effectiveAddress, STAKING_MANAGER_ADDRESS] : undefined,
    query: {
      enabled: !!effectiveAddress && isConnected && STAKING_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  }) as { data: bigint | undefined; refetch: () => void }

  // AGC: Read lock status for airdrop recipients (V5+)
  const { data: rawLockInfo, refetch: refetchLockInfo } = useReadContract({
    address: STAKING_MANAGER_ADDRESS,
    abi: STAKING_MANAGER_ABI,
    functionName: 'isLocked',
    args: effectiveAddress ? [effectiveAddress] : undefined,
    query: {
      enabled: !!effectiveAddress && isConnected && STAKING_MANAGER_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Process lock info - returns (bool locked, uint256 unlockTime)
  const lockInfo = rawLockInfo && Array.isArray(rawLockInfo) && rawLockInfo.length >= 2
    ? {
        isLocked: rawLockInfo[0] as boolean,
        unlockTime: Number(rawLockInfo[1]) * 1000, // AGC: Convert to milliseconds
        unlockTimeRaw: rawLockInfo[1] as bigint,
      }
    : { isLocked: false, unlockTime: 0, unlockTimeRaw: BigInt(0) }

  // AGC: Calculate remaining lock time
  const lockTimeRemaining = lockInfo.isLocked 
    ? Math.max(0, lockInfo.unlockTime - Date.now())
    : 0
  
  const lockDaysRemaining = Math.ceil(lockTimeRemaining / (1000 * 60 * 60 * 24))

  // AGC: Wait for transaction receipt
  const { data: receipt, isLoading: isWaitingForReceipt } = useWaitForTransactionReceipt({
    hash: pendingTxHash || undefined,
  })

  // AGC: Refetch staking info when transaction completes
  useEffect(() => {
    if (receipt) {
      console.log('✅ Transaction receipt received, refetching staking info...')
      // AGC: Wait a moment for blockchain state to propagate before refetching
      const timeout = setTimeout(() => {
        refetchStakingInfo()
        refetchAllowance()
        console.log('✅ Staking info refetched after transaction')
      }, 2000) // AGC: Wait 2 seconds for state to propagate
      
      setPendingTxHash(null)
      
      return () => clearTimeout(timeout)
    }
  }, [receipt, refetchStakingInfo, refetchAllowance])

  // AGC: Approve OGB tokens for StakingManager
  const approve = async (amount: string) => {
    if (!effectiveAddress) throw new Error('No address connected')
    if (STAKING_MANAGER_ADDRESS === '0x0000000000000000000000000000000000000000') throw new Error('StakingManager address not configured')

    const amountWei = parseEther(amount)
    const hash = await writeContractAsync({
      address: OGB_TOKEN_ADDRESS,
      abi: OGB_TOKEN_ABI,
      functionName: 'approve',
      args: [STAKING_MANAGER_ADDRESS, amountWei],
    })
    
    setPendingTxHash(hash)
    return hash
  }

  // AGC: Stake OGB tokens
  const stake = async (amount: string) => {
    if (!effectiveAddress) throw new Error('No address connected')
    if (STAKING_MANAGER_ADDRESS === '0x0000000000000000000000000000000000000000') throw new Error('StakingManager address not configured')

    const amountWei = parseEther(amount)
    
    // AGC: Check if approval is needed
    const currentAllowance = allowance || 0n
    if (currentAllowance < amountWei) {
      // AGC: Approve first (approve max amount for better UX)
      const maxApproval = parseEther('1000000') // AGC: Approve 1M OGB (should be enough)
      const approveHash = await writeContractAsync({
        address: OGB_TOKEN_ADDRESS,
        abi: OGB_TOKEN_ABI,
        functionName: 'approve',
        args: [STAKING_MANAGER_ADDRESS, maxApproval],
      })
      setPendingTxHash(approveHash)
      
      // AGC: Wait for approval transaction
      // Note: In production, you'd want to wait for this receipt before staking
      // For now, we'll proceed and let the contract handle the revert if needed
    }

    const hash = await writeContractAsync({
      address: STAKING_MANAGER_ADDRESS,
      abi: STAKING_MANAGER_ABI,
      functionName: 'stake',
      args: [amountWei],
    })
    
    setPendingTxHash(hash)
    return hash
  }

  // AGC: Request unstake
  const requestUnstake = async (amount: string) => {
    if (!effectiveAddress) throw new Error('No address connected')
    if (STAKING_MANAGER_ADDRESS === '0x0000000000000000000000000000000000000000') throw new Error('StakingManager address not configured')

    const amountWei = parseEther(amount)
    
    const contractConfig = {
      address: STAKING_MANAGER_ADDRESS,
      abi: STAKING_MANAGER_ABI,
      functionName: 'requestUnstake' as const,
      args: [amountWei],
    }

    try {
      // AGC: Try direct call first
      const hash = await writeContractAsync(contractConfig)
      setPendingTxHash(hash)
      return hash
    } catch (error: any) {
      console.warn('⚠️ [Staking] Direct unstake request failed, attempting gas estimation fallback...', error)
      
      // AGC: If wallet-side estimation failed, try manual gas estimation
      if (!publicClient || !effectiveAddress) {
        // AGC: Extract better error message
        const errorMessage = error?.shortMessage || error?.message || error?.reason || 'Transaction failed'
        throw new Error(`Unstake request failed: ${errorMessage}`)
      }

      try {
        // AGC: Estimate gas manually to get better error messages
        console.log('🔍 [Staking] Estimating gas manually for unstake...')
        const gasEstimate = await publicClient.estimateContractGas({
          ...contractConfig,
          account: effectiveAddress
        })
        console.log('✅ [Staking] Gas estimate:', gasEstimate.toString())

        // AGC: Get fee data
        let feeOverrides: {
          gas?: bigint
          maxFeePerGas?: bigint
          maxPriorityFeePerGas?: bigint
          gasPrice?: bigint
        } = { gas: gasEstimate }

        try {
          // AGC: Prefer EIP-1559 fee data when available
          const feeData = await publicClient.estimateFeesPerGas()
          feeOverrides = {
            gas: gasEstimate,
            maxFeePerGas: feeData.maxFeePerGas,
            maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
          }
          console.log('✅ [Staking] Fee data estimated:', feeOverrides)
        } catch (feeError) {
          console.warn('⚠️ [Staking] Failed to estimate EIP-1559 fees, using gasPrice', feeError)
          try {
            const gasPrice = await publicClient.getGasPrice()
            feeOverrides = {
              gas: gasEstimate,
              gasPrice
            }
          } catch (gasPriceError) {
            console.warn('⚠️ [Staking] Unable to fetch gasPrice fallback:', gasPriceError)
          }
        }

        // AGC: Retry with manual gas estimation
        const hash = await writeContractAsync({
          ...contractConfig,
          ...feeOverrides
        })
        setPendingTxHash(hash)
        console.log('✅ [Staking] Unstake request transaction submitted (with manual gas):', hash)
        return hash
      } catch (gasError: any) {
        // AGC: Gas estimation failed - this means the transaction would revert
        const revertReason = gasError?.shortMessage || gasError?.message || gasError?.reason || 'Transaction would revert'
        console.error('❌ [Staking] Gas estimation failed:', revertReason)
        
        // AGC: Check for common revert reasons
        if (revertReason.includes('locked') || revertReason.includes('Locked') || revertReason.includes('lockup')) {
          throw new Error('Tokens are locked and cannot be unstaked yet. Airdropped tokens are locked for 60 days.')
        } else if (revertReason.includes('insufficient') || revertReason.includes('Insufficient')) {
          throw new Error('Insufficient staked balance')
        } else if (revertReason.includes('paused') || revertReason.includes('Paused')) {
          throw new Error('Unstaking is currently paused')
        } else {
          throw new Error(`Unstake request failed: ${revertReason}`)
        }
      }
    }
  }

  // AGC: Complete unstake
  const completeUnstake = async (requestIndex: number) => {
    if (!effectiveAddress) throw new Error('No address connected')
    if (STAKING_MANAGER_ADDRESS === '0x0000000000000000000000000000000000000000') throw new Error('StakingManager address not configured')

    const hash = await writeContractAsync({
      address: STAKING_MANAGER_ADDRESS,
      abi: STAKING_MANAGER_ABI,
      functionName: 'completeUnstake',
      args: [BigInt(requestIndex)],
    })
    
    setPendingTxHash(hash)
    return hash
  }

  // AGC: Helper to get tier name
  const getTierName = (tier?: 0 | 1 | 2): 'NONE' | 'ACCESS' | 'MAX' => {
    if (tier === 0) return 'NONE'
    if (tier === 1) return 'ACCESS'
    if (tier === 2) return 'MAX'
    return 'NONE'
  }

  // AGC: Helper to format staking score as percentage
  const getStakingScorePercent = (stakingScore?: bigint): number => {
    if (!stakingScore) return 0
    // AGC: stakingScore is scaled to 1e18, convert to percentage
    return Number((stakingScore * 100n) / (10n ** 18n))
  }

  return {
    // AGC: Data
    stakingInfo: stakingInfo ? {
      ...stakingInfo,
      tier: getTierName(stakingInfo.tier),
      stakingScorePercent: getStakingScorePercent(stakingInfo.stakingScore),
    } : undefined,
    ogbBalance: ogbBalance || 0n,
    allowance: allowance || 0n,
    isConnected,
    address: effectiveAddress,
    
    // AGC: Lock info for airdrop recipients
    lockInfo: {
      isLocked: lockInfo.isLocked,
      unlockTime: lockInfo.unlockTime,
      daysRemaining: lockDaysRemaining,
      unlockDate: lockInfo.unlockTime > 0 ? new Date(lockInfo.unlockTime) : null,
    },
    
    // AGC: Actions
    approve,
    stake,
    requestUnstake,
    completeUnstake,
    refetchStakingInfo,
    refetchAllowance,
    refetchLockInfo,
    
    // AGC: Loading states
    isPending: isPending || isWaitingForReceipt,
    pendingTxHash,
    
    // AGC: Contract addresses
    stakingManagerAddress: STAKING_MANAGER_ADDRESS,
    ogbTokenAddress: OGB_TOKEN_ADDRESS,
    
    // AGC: Error state (expose contract read error)
    error: stakingInfoError?.message,
  }
}

